import { FC, ReactNode, createContext, useCallback, useMemo, useState } from 'react';
import { ITrainingBlock } from '../../../models/allenamenti';

/**
 * Contenuto copiabile di un blocco (F1 copia/incolla) — SOLO il contenuto,
 * MAI la posizione: `localDate`/`teamId`/`slot`/`order` arrivano sempre dal
 * giorno target al momento dell'incolla (vedi handlePasteBlock in
 * AllenamentiPage), non dal blocco copiato.
 */
export type IBlockClipboardContent = Pick<
  ITrainingBlock,
  | 'categoryIds'
  | 'subcategoryIds'
  | 'title'
  | 'durationMinutes'
  | 'responsible'
  | 'fieldWidthM'
  | 'fieldLengthM'
  | 'playersOnField'
  | 'effectiveMinutes'
  | 'malloWeight'
  | 'flowType'
>;

export interface IBlockClipboardContextValue {
  /** `null` = appunti vuoti: il bottone "Incolla" non deve comparire. */
  clipboard: IBlockClipboardContent | null;
  copy: (block: ITrainingBlock) => void;
}

/**
 * `null` di default: un consumo fuori dal Provider è un errore di
 * programmazione (vedi useBlockClipboard), non uno stato legittimo da gestire
 * silenziosamente con un fallback vuoto.
 */
export const BlockClipboardContext = createContext<IBlockClipboardContextValue | null>(null);

/**
 * Appunti in memoria del modulo Allenamenti (F1).
 *
 * Monta SOPRA la vista settimana (AllenamentiPage, non dentro
 * `useAllenamentiWeek`): lo stato vive qui, non nell'hook che si "ricalcola"
 * a ogni cambio di `weekStart`, così un blocco copiato resta disponibile
 * navigando avanti e indietro fra le settimane. Si perde solo al reload della
 * pagina — nessuna persistenza voluta.
 */
export const BlockClipboardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [clipboard, setClipboard] = useState<IBlockClipboardContent | null>(null);

  const copy = useCallback((block: ITrainingBlock) => {
    setClipboard({
      categoryIds: block.categoryIds,
      subcategoryIds: block.subcategoryIds,
      title: block.title,
      durationMinutes: block.durationMinutes,
      responsible: block.responsible,
      fieldWidthM: block.fieldWidthM,
      fieldLengthM: block.fieldLengthM,
      playersOnField: block.playersOnField,
      effectiveMinutes: block.effectiveMinutes,
      malloWeight: block.malloWeight,
      flowType: block.flowType,
    });
  }, []);

  const value = useMemo(() => ({ clipboard, copy }), [clipboard, copy]);

  return (
    <BlockClipboardContext.Provider value={value}>{children}</BlockClipboardContext.Provider>
  );
};
